import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
    Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, FormControlLabel, Checkbox, Link, TableSortLabel, Tooltip
} from '@mui/material';
import { makeRequest } from '../../api';
import { backend_url } from '../../config/variables';
import { OverRow, StatPair } from "../../interfaces/OverRow";
import { MomentumDialog } from "../../components/MomentumDialog";

// Descarga de la tabla de alertas V3 con su desenlace. Va como enlace directo y
// no por `makeRequest` a propósito: el backend responde con Content-Disposition,
// así que el navegador guarda el archivo solo. Bajarlo por axios obligaría a
// armar un Blob y una URL temporal para nada.
//
// Sale en Excel si la VM tiene `exceljs`; si no, el mismo endpoint responde CSV
// (lo dice en la cabecera X-Formato-Real). Por eso el texto del botón no promete
// una extensión concreta.
const URL_EXPORT_V3 = `${backend_url}/api/over/alerts/v3/export`;

// Partidos de las últimas 48 h con su minuto a minuto. `matchstatssnapshots`
// tiene TTL de 48 h, así que este archivo es la única forma de conservar el
// detalle. Lo genera un cron cada pocas horas y lo deja en disco: aquí sólo se
// descarga lo ya hecho, por eso el botón dice de cuándo es.
const URL_EXPORT_48H = `${backend_url}/api/export/partidos-48h`;

interface Estado48h { listo: boolean; generando: boolean; antiguedadMin: number | null; formato: string | null; bytes: number | null; resumen?: { partidos: number; filasMinuto: number } | null }

const antiguedad = (min: number | null): string => {
    if (min == null) return '';
    if (min < 60) return `hace ${min} min`;
    const h = Math.floor(min / 60);
    return h < 48 ? `hace ${h} h` : `hace ${Math.floor(h / 24)} d`;
};

const pair = (p?: StatPair | null) => (p && (p.h != null || p.a != null) ? `${p.h ?? '-'}-${p.a ?? '-'}` : '—');
const pct = (p: number | null) => (p != null ? `${Math.round(p * 100)}%` : '—');

// Color/flag by edge (probModel - probMarket): rojo sin valor, amarillo medio, verde alto, 🔥 top.
const edgeColor = (e: number | null): string | undefined => {
    if (e == null) return undefined;
    if (e >= 0.10) return '#b9f6ca';
    if (e >= 0.05) return '#dcedc8';
    if (e >= 0) return '#fff9c4';
    return '#ffcdd2';
};
const edgeFlag = (e: number | null) => (e == null ? '' : e >= 0.10 ? '🔥' : e >= 0.05 ? '🟢' : e >= 0 ? '🟡' : '🔴');
const edgeText = (e: number | null) => (e != null ? `${e >= 0 ? '+' : ''}${(e * 100).toFixed(1)}%` : '—');

// Momentum cell: slope (↑↓→ con color) + dominante (H/A/=, color del equipo).
// SLOPE_TH = umbral para considerar la pendiente "real" — debajo de eso, plano.
const SLOPE_TH = 0.5;
const momentumCell = (r: OverRow): React.ReactNode => {
    const m = r.momentum;
    if (!m || m.slopeDatt == null) return '—';
    const s = m.slopeDatt;
    const arrow = s > SLOPE_TH ? '↑' : s < -SLOPE_TH ? '↓' : '→';
    const arrowColor = s > SLOPE_TH ? '#2e7d32' : s < -SLOPE_TH ? '#c62828' : '#90a4ae';
    const domStyle: React.CSSProperties = m.dominant === 'home' ? { color: '#1976d2', fontWeight: 600 }
        : m.dominant === 'away' ? { color: '#e64a19', fontWeight: 600 }
        : { color: '#90a4ae' };
    const domLetter = m.dominant === 'home' ? 'H' : m.dominant === 'away' ? 'A' : '=';
    return (
        <span>
            <span style={{ color: arrowColor, fontWeight: 600 }}>{arrow} {s.toFixed(1)}</span>
            <span style={{ marginLeft: 6, ...domStyle }}>{domLetter}</span>
        </span>
    );
};

type Align = 'left' | 'center';
interface Column {
    key: string;
    label: string;
    align: Align;
    sortable: boolean;
    tooltip?: string;
    render: (r: OverRow) => React.ReactNode;
}

const columns: Column[] = [
    { key: 'flag', label: '', align: 'center', sortable: false, tooltip: 'Indicador de valor según el edge: 🔥 ≥10% · 🟢 ≥5% · 🟡 ≥0% · 🔴 <0%', render: (r) => edgeFlag(r.edge) },
    { key: 'league', label: 'Liga', align: 'left', sortable: true, render: (r) => r.league },
    { key: 'match', label: 'Partido', align: 'left', sortable: true, render: (r) => r.competitors?.join(' vs ') || r.matchID },
    { key: 'minute', label: 'Min', align: 'center', sortable: true, tooltip: 'Minuto actual del partido (1T = primer tiempo, 2T = segundo tiempo)', render: (r) => `${r.minute}'${r.ht ? ' (2T)' : ' (1T)'}` },
    { key: 'totGoals', label: 'Marcador', align: 'center', sortable: true, tooltip: 'Marcador actual (local-visitante). Ordena por goles totales.', render: (r) => `${r.score[0]}-${r.score[1]}` },
    { key: 'oddsNextGoal', label: 'Cuota gol+', align: 'center', sortable: true, tooltip: 'Cuota de Novibet para "Más de (goles_actuales + 0.5)", es decir que haya al menos 1 gol más. "—" = Novibet no ofrece esa línea exacta ahora.', render: (r) => r.oddsNextGoal ?? '—' },
    { key: 'probModel', label: 'Prob modelo', align: 'center', sortable: true, tooltip: 'Probabilidad estimada por nuestro modelo (Poisson) de que haya ≥1 gol más antes del final. P = 1 − e^(−λ).', render: (r) => <strong>{pct(r.probModel)}</strong> },
    { key: 'probMarket', label: 'Prob mercado', align: 'center', sortable: true, tooltip: 'Probabilidad implícita en la cuota ajustada por el margen del bookie: 0.915 / cuota (≈ remueve ~8.5% de overround). Es la estimación "real" del mercado, comparable al modelo.', render: (r) => pct(r.probMarket) },
    { key: 'edge', label: 'Edge', align: 'center', sortable: true, tooltip: 'Ventaja = Prob modelo − Prob mercado. Positivo = el mercado subestima la probabilidad (posible valor). Solo existe si hay cuota.', render: (r) => <strong>{edgeText(r.edge)}</strong> },
    { key: 'timeRemaining', label: 'T. restante', align: 'center', sortable: true, tooltip: 'Minutos estimados hasta el final = FT estimado (avgTimer histórico por liga + diferencia de goles) − minuto actual.', render: (r) => (r.timeRemaining != null ? `${r.timeRemaining}'` : '—') },
    { key: 'datt', label: 'Ataq. pelig. (últ)', align: 'center', sortable: true, tooltip: 'Ataques peligrosos (ambos equipos sumados) ACUMULADOS en la ventana reciente de snapshots (~últimos minutos). Es un Δ de intensidad, NO el minuto del último ataque.', render: (r) => (r.intensity ? r.intensity.dattLast : '—') },
    { key: 'shots', label: 'Tiros (últ)', align: 'center', sortable: true, tooltip: 'Tiros TOTALES (al arco + fuera del arco, ambos equipos sumados) en la ventana reciente de snapshots. Δ de intensidad, no el minuto del último tiro.', render: (r) => (r.intensity ? r.intensity.shotsLast : '—') },
    { key: 'momentum', label: 'Mom', align: 'center', sortable: true, tooltip: 'Momentum: pendiente de intensidad (últimos 2 min vs 4 anteriores). ↑ = presión subiendo, ↓ = bajando. Letra = equipo dominante: H (local) · A (visitante) · = (parejo). Persistido en cada alerta para backtest; NO afecta el modelo todavía.', render: (r) => momentumCell(r) },
    { key: 'poss', label: 'Posesión', align: 'center', sortable: true, tooltip: 'Posesión local-visitante (%) según el último snapshot. "—" = sin datos en vivo para ese partido.', render: (r) => pair(r.stats?.poss) },
    { key: 'link', label: 'Link', align: 'center', sortable: false, tooltip: 'Abre el partido en Novibet', render: (r) => (r.link ? <Link href={r.link} target="_blank" rel="noopener">ver</Link> : '—') },
];

// Sort value per column key (null/strings handled in the comparator).
const accessors: Record<string, (r: OverRow) => number | string | null> = {
    league: (r) => r.league,
    match: (r) => (r.competitors || []).join(' '),
    minute: (r) => r.minute,
    totGoals: (r) => r.totGoals,
    oddsNextGoal: (r) => r.oddsNextGoal,
    probModel: (r) => r.probModel,
    probMarket: (r) => r.probMarket,
    edge: (r) => r.edge,
    timeRemaining: (r) => r.timeRemaining,
    datt: (r) => (r.intensity ? r.intensity.dattLast : null),
    shots: (r) => (r.intensity ? r.intensity.shotsLast : null),
    momentum: (r) => r.momentum?.slopeDatt ?? null,
    poss: (r) => r.stats?.poss?.h ?? null,
};

type Order = 'asc' | 'desc';

const OverTable = () => {
    const [data, setData] = useState<OverRow[]>([]);
    const [onlyWithEdge, setOnlyWithEdge] = useState<boolean>(false);
    const [openMatchID, setOpenMatchID] = useState<string | null>(null);
    const [updatedAt, setUpdatedAt] = useState<string>('');
    const [orderBy, setOrderBy] = useState<string>('edge');
    const [order, setOrder] = useState<Order>('desc');
    const [est48h, setEst48h] = useState<Estado48h | null>(null);

    const fetch48h = useCallback(async () => {
        try {
            const res: any = await makeRequest('api/export/partidos-48h/estado', { method: 'get' });
            if (res.status === 200) setEst48h(res.data as Estado48h);
        } catch (e) {
            setEst48h(null);   // endpoint aún no desplegado: el botón se oculta solo
        }
    }, []);

    const fetchData = useCallback(async () => {
        try {
            const res: any = await makeRequest('api/over/ranking', { method: 'get' });
            if (res.status === 200) {
                setData(res.data as OverRow[]);
                setUpdatedAt(new Date().toLocaleTimeString());
            }
        } catch (e) {
            // makeRequest ya loggea
        }
    }, []);

    useEffect(() => {
        fetchData();
        const id = setInterval(fetchData, 30000); // refresco automático (data en vivo)
        return () => clearInterval(id);
    }, [fetchData]);

    useEffect(() => {
        fetch48h();
        const id = setInterval(fetch48h, 300000);  // el archivo cambia cada pocas horas: no hace falta más
        return () => clearInterval(id);
    }, [fetch48h]);

    const handleSort = (key: string) => {
        if (orderBy === key) setOrder(order === 'asc' ? 'desc' : 'asc');
        else { setOrderBy(key); setOrder('desc'); }
    };

    const rows = useMemo(() => {
        const filtered = onlyWithEdge ? data.filter((r) => r.edge != null) : data;
        const acc = accessors[orderBy];
        if (!acc) return filtered;
        return [...filtered].sort((a, b) => {
            const va = acc(a);
            const vb = acc(b);
            if (va == null && vb == null) return 0;
            if (va == null) return 1;   // nulls siempre al final
            if (vb == null) return -1;
            const cmp = typeof va === 'string' ? va.localeCompare(vb as string) : (va as number) - (vb as number);
            return order === 'asc' ? cmp : -cmp;
        });
    }, [data, onlyWithEdge, orderBy, order]);

    return (
        <div>
            <Paper variant="outlined" style={{ padding: '10px 14px', margin: '8px 0', fontFamily: 'monospace', fontSize: 13, whiteSpace: 'pre-line', background: '#fafafa' }}>
                {[
                    'Modelo de valor (Over — "1 gol más"):',
                    '  P(≥1 gol más) = 1 − e^(−λ)',
                    '  λ = 0.027 × min_restantes × intensidad',
                    '  intensidad = limitar[0.4 … 2.5]( 0.3 + 0.3·(ataq.pelig./min ÷ 1.0) + 0.4·(tiros/min ÷ 0.30) )   tiros = al arco + fuera',
                    '  edge = P(modelo) − P(mercado)        P(mercado) = 0.915 / cuota   (≈ remueve ~8.5% de margen del bookie)',
                    '  min_restantes = FT_estimado(avgTimer por liga+dif. goles) − minuto_actual',
                ].join('\n')}
            </Paper>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center', margin: '10px 0', flexWrap: 'wrap' }}>
                <Button variant="contained" color="primary" onClick={fetchData}>Refresh</Button>
                <Tooltip title="Todas las alertas del canal V3 con su desenlace: señales del momento (tempo, índice, SPP, momentum), las dos cuotas de la línea y si cayó el gol. Se genera al momento desde la base.">
                    <Button variant="outlined" color="secondary" href={URL_EXPORT_V3} download>
                        ⬇ Alertas V3
                    </Button>
                </Tooltip>
                {est48h && (
                    <Tooltip title={est48h.listo
                        ? `Partidos de las últimas 48 h con su minuto a minuto: marcador, estadísticas por equipo, cuotas over/under de Novibet y las alertas y órdenes del periodo. ${est48h.resumen ? `${est48h.resumen.partidos} partidos · ${est48h.resumen.filasMinuto.toLocaleString()} filas · ` : ''}${est48h.formato === 'xlsx' ? 'Excel' : 'CSV'}${est48h.bytes ? ` · ${(est48h.bytes / 1e6).toFixed(1)} MB` : ''}. Se regenera solo cada 3 horas.`
                        : 'Todavía no hay archivo generado. El primero sale un par de minutos después de arrancar el backend.'}>
                        <span>
                            <Button variant="outlined" color="secondary" href={URL_EXPORT_48H} download disabled={!est48h.listo}>
                                ⬇ Partidos 48 h {est48h.listo ? `(${antiguedad(est48h.antiguedadMin)})` : est48h.generando ? '(generando…)' : '(sin archivo)'}
                            </Button>
                        </span>
                    </Tooltip>
                )}
                <FormControlLabel
                    control={<Checkbox checked={onlyWithEdge} onChange={(e) => setOnlyWithEdge(e.target.checked)} />}
                    label="Solo con edge (cuota disponible)"
                />
                <span style={{ color: '#888' }}>
                    {data.length} partidos · {data.filter((r) => r.edge != null).length} con edge · act. {updatedAt}
                </span>
                <span style={{ color: '#888' }}>🔥 ≥10% · 🟢 ≥5% · 🟡 ≥0% · 🔴 &lt;0%</span>
            </div>
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {columns.map((c) => {
                                const inner = c.sortable ? (
                                    <TableSortLabel
                                        active={orderBy === c.key}
                                        direction={orderBy === c.key ? order : 'desc'}
                                        onClick={() => handleSort(c.key)}
                                    >
                                        {c.label}
                                    </TableSortLabel>
                                ) : <span>{c.label}</span>;
                                return (
                                    <TableCell key={c.key} align={c.align} sortDirection={orderBy === c.key ? order : false}>
                                        {c.tooltip
                                            ? <Tooltip title={c.tooltip} arrow placement="top"><span>{inner}</span></Tooltip>
                                            : inner}
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((r) => (
                            <TableRow
                                key={r.matchID}
                                hover
                                style={{ backgroundColor: edgeColor(r.edge), cursor: 'pointer' }}
                                onClick={(e) => {
                                    // ignore clicks on the inline link to Novibet
                                    if ((e.target as HTMLElement).closest('a')) return;
                                    setOpenMatchID(r.matchID);
                                }}
                            >
                                {columns.map((c) => (
                                    <TableCell key={c.key} align={c.align}>{c.render(r)}</TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <MomentumDialog matchID={openMatchID} onClose={() => setOpenMatchID(null)} />
        </div>
    );
};

export { OverTable };
